import { translate } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { frameContentToClipboardText } from "./frame-copy";
import {
  docMarkdown,
  isDocContent,
} from "@/components/shapes/doc-frame/doc-content";
import { fileExtension, fileStem } from "@/lib/file-kinds";
import { previewSource } from "@/lib/frame-preview-source";
import { saveFile } from "@/lib/save-file";
import { isOwnUploadUrl, ownUploadUrl } from "@/lib/upload-url";
import {
  BASE_STYLE,
  LIGHT_OVERRIDE,
} from "@/components/shapes/html-frame/html-frame-styles";
import { HTML_FRAME_DESIGN_SCRIPT } from "@/components/shapes/html-frame/html-frame-design-runtime";
import { getInitialThemeMode } from "@/components/shapes/html-frame/html-frame-theme";
import type { FrameType } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";

/** Types whose content text downloads as a plain-text file. */
const TEXT_DOWNLOAD_TYPES: ReadonlySet<FrameType> = new Set([
  "code",
  "note",
  "search",
  "email",
  "newsletter",
]);

/** Every type `frameDownloadOptions` can offer a file for. */
const DOWNLOAD_TYPES: ReadonlySet<FrameType> = new Set([
  "doc",
  "markdown",
  "html",
  "chart",
  ...TEXT_DOWNLOAD_TYPES,
]);

export interface FrameDownloadOption {
  /** Translated when the options are built, at render time. */
  label: string;
  extension: string;
  mimeType: string;
  fileName: string;
  createBlob: (root: HTMLElement | null) => Promise<Blob>;
}

function safeFileName(title: string): string {
  return (
    title
      .trim()
      .split("")
      .map((character) => (character.charCodeAt(0) < 32 ? "-" : character))
      .join("")
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/[. ]+$/g, "")
      .slice(0, 100) || "frame"
  );
}

/**
 * The bytes of one of our uploads: no cookies or referrer, and the
 * response must still be the upload after redirects.
 */
async function fetchOwnUpload(url: string): Promise<Blob> {
  const response = await fetch(url, {
    credentials: "omit",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok || !isOwnUploadUrl(response.url)) {
    void response.body?.cancel().catch(() => undefined);
    throw new Error(translate(framesSharedMessages, "tryAgain"));
  }
  return response.blob();
}

/** A file frame's upload, saved under its original name with its bytes. */
function fileOption(frame: FrameData): FrameDownloadOption | null {
  if (frame.frameType !== "file") return null;
  const source = previewSource(frame);
  const url = ownUploadUrl(source.url);
  if (!url) return null;
  const name =
    typeof source.name === "string" && source.name.trim()
      ? source.name
      : frame.title;
  const found = fileExtension(name);
  const extension = /^[a-z0-9]{1,16}$/.test(found) ? found : "";
  const stem = extension ? fileStem(name) : name;
  return {
    label: translate(framesSharedMessages, "downloadFile"),
    extension,
    mimeType:
      typeof source.mimeType === "string" && source.mimeType
        ? source.mimeType
        : "application/octet-stream",
    fileName: extension
      ? `${safeFileName(stem)}.${extension}`
      : safeFileName(stem),
    createBlob: () => fetchOwnUpload(url),
  };
}

function textOption(
  label: string,
  extension: string,
  mimeType: string,
  fileName: string,
  content: string | (() => string),
): FrameDownloadOption {
  return {
    label,
    extension,
    mimeType,
    fileName: `${fileName}.${extension}`,
    createBlob: async () =>
      new Blob([typeof content === "string" ? content : content()], {
        type: mimeType,
      }),
  };
}

function csvCell(value: string): string {
  const trimmed = value.trimStart();
  const formula =
    /^[=+@]/.test(trimmed) ||
    (trimmed.startsWith("-") && !/^-\d+(?:\.\d+)?$/.test(trimmed));
  const safe = formula ? `'${value}` : value;
  return `"${safe.replace(/"/g, '""')}"`;
}

function standaloneHtml(frame: FrameData, source: string): string {
  const exportDocument = new DOMParser().parseFromString(source, "text/html");
  const mode = getInitialThemeMode(frame.metadata ?? {});
  const isDark =
    mode === "dark" ||
    (mode === "app" && document.documentElement.classList.contains("dark"));
  exportDocument.head.insertAdjacentHTML(
    "afterbegin",
    BASE_STYLE + (isDark ? "" : LIGHT_OVERRIDE) + HTML_FRAME_DESIGN_SCRIPT,
  );
  if (!exportDocument.head.querySelector("meta[charset]")) {
    const meta = exportDocument.createElement("meta");
    meta.setAttribute("charset", "utf-8");
    exportDocument.head.prepend(meta);
  }
  return `<!doctype html>\n${exportDocument.documentElement.outerHTML}`;
}

function tableCsv(table: HTMLTableElement): string {
  return Array.from(table.rows)
    .map((row) =>
      Array.from(row.cells)
        .map((cell) => csvCell(cell.textContent?.trim() ?? ""))
        .join(","),
    )
    .join("\r\n");
}

/**
 * A full frame's download formats. `rendered`: the frame's live body is on
 * screen, which a chart's PNG captures; without it only data formats are
 * offered.
 */
export function frameDownloadOptions(
  frame: FrameData,
  { rendered = true }: { rendered?: boolean } = {},
): FrameDownloadOption[] {
  const file = fileOption(frame);
  if (file) return [file];
  const name = safeFileName(frame.title);
  const content = frame.content ?? {};
  // A Doc downloads the markdown snapshot written with every save.
  const markdown =
    frame.frameType === "doc" || isDocContent(content)
      ? docMarkdown(frame.frameType, content)
      : frame.frameType === "markdown" && typeof content.markdown === "string"
        ? content.markdown
        : null;
  if (markdown !== null) {
    return [
      textOption(
        translate(framesSharedMessages, "downloadMarkdown"),
        "md",
        "text/markdown;charset=utf-8",
        name,
        markdown,
      ),
    ];
  }
  if (frame.frameType === "html" && typeof content.html === "string") {
    const options = [
      textOption(
        translate(framesSharedMessages, "downloadHtml"),
        "html",
        "text/html;charset=utf-8",
        name,
        () => standaloneHtml(frame, content.html as string),
      ),
    ];
    const tables = /<table[\s>]/i.test(content.html)
      ? Array.from(
          new DOMParser()
            .parseFromString(content.html, "text/html")
            .querySelectorAll("table"),
        )
      : [];
    tables.forEach((table, index) => {
      const tableName =
        tables.length === 1 ? name : `${name}-table-${index + 1}`;
      options.push(
        textOption(
          tables.length === 1
            ? translate(framesSharedMessages, "downloadTableCsv")
            : translate(framesSharedMessages, "downloadNumberedTableCsv", {
                number: index + 1,
              }),
          "csv",
          "text/csv;charset=utf-8",
          tableName,
          tableCsv(table),
        ),
      );
    });
    return options;
  }
  if (frame.frameType === "chart") {
    const options: FrameDownloadOption[] = [];
    if (Array.isArray(content.points)) {
      const rows = content.points.flatMap((item) => {
        if (!item || typeof item !== "object") return [];
        const point = item as Record<string, unknown>;
        if (typeof point.value !== "number" || !Number.isFinite(point.value))
          return [];
        return [
          `${csvCell(typeof point.label === "string" ? point.label : "")},${point.value}`,
        ];
      });
      if (rows.length > 0) {
        options.push(
          textOption(
            translate(framesSharedMessages, "downloadChartCsv"),
            "csv",
            "text/csv;charset=utf-8",
            name,
            ["Label,Value", ...rows].join("\r\n"),
          ),
        );
        if (!rendered) return options;
        options.unshift({
          label: translate(framesSharedMessages, "downloadChartPng"),
          extension: "png",
          mimeType: "image/png",
          fileName: `${name}.png`,
          createBlob: async (root) => {
            const chart = root?.querySelector<HTMLElement>(
              "[data-testid='chart-frame']",
            );
            if (!chart)
              throw new Error(
                translate(framesSharedMessages, "chartNotVisible"),
              );
            const { toBlob } = await import("html-to-image");
            const background = getComputedStyle(document.documentElement)
              .getPropertyValue("--color-surface")
              .trim();
            const blob = await toBlob(chart, {
              pixelRatio: 2,
              backgroundColor: background || "#1c1c22",
            });
            if (!blob)
              throw new Error(
                translate(framesSharedMessages, "chartRenderFailed"),
              );
            return blob;
          },
        });
      }
    }
    return options;
  }

  if (TEXT_DOWNLOAD_TYPES.has(frame.frameType)) {
    const text = frameContentToClipboardText(frame);
    if (text) {
      return [
        textOption(
          translate(framesSharedMessages, "downloadText"),
          "txt",
          "text/plain;charset=utf-8",
          name,
          text,
        ),
      ];
    }
  }
  return [];
}

/**
 * Whether a frame's menu offers Download: a full frame when it has a
 * format, a card-only frame (content not loaded yet) when its type can
 * have one. A file frame offers its upload (its card carries the URL);
 * image frames have none.
 */
export function frameMayDownload(frame: FrameData): boolean {
  if (frame.frameType === "file") return fileOption(frame) !== null;
  if (frame.contentState === "card") return DOWNLOAD_TYPES.has(frame.frameType);
  return frameDownloadOptions(frame, { rendered: false }).length > 0;
}

/**
 * Whether the frame's formats depend on its content, so its Download is a
 * choice only once the content is known: an HTML page's tables, a rendered
 * chart's PNG beside its CSV. Every other type has one format.
 */
export function frameDownloadVaries(
  frame: FrameData,
  { rendered }: { rendered: boolean },
): boolean {
  return (
    frame.frameType === "html" || (rendered && frame.frameType === "chart")
  );
}

export async function saveFrameDownload(
  option: FrameDownloadOption,
  root: HTMLElement | null,
): Promise<boolean> {
  return saveFile(
    await option.createBlob(root),
    option.fileName,
    option.extension,
  );
}
